package org.gymly.util

import kotlin.time.Instant

/** Formato de valor que emite el campo de fecha al elegir en el calendario. */
enum class AdminDateOutputFormat {
    /** yyyy-MM-dd (API / backend). */
    ISO,
    /** dd-MM-yyyy (visual admin / reportes). */
    DISPLAY,
}

/** Normaliza texto libre a ISO `yyyy-MM-dd`, o null si no es parseable. Acepta dd-MM-yyyy y dd/MM/yyyy. */
fun parseFlexibleDateToIso(raw: String): String? {
    val t = raw.trim()
    if (t.isEmpty()) return null
    if (Regex("""^\d{4}-\d{2}-\d{2}$""").matches(t)) {
        return if (isValidIsoParts(t)) t else null
    }
    val m = Regex("""^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$""").matchEntire(t) ?: return null
    val day = m.groupValues[1].toIntOrNull() ?: return null
    val month = m.groupValues[2].toIntOrNull() ?: return null
    val year = m.groupValues[3].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null
    val iso = "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
    return if (isValidIsoParts(iso)) iso else null
}

fun formatIsoToDisplayDate(iso: String): String {
    val parts = iso.trim().take(10).split("-")
    if (parts.size != 3) return iso
    return "${parts[2]}-${parts[1]}-${parts[0]}"
}

fun formatDateForOutput(iso: String, format: AdminDateOutputFormat): String =
    when (format) {
        AdminDateOutputFormat.ISO -> iso.take(10)
        AdminDateOutputFormat.DISPLAY -> formatIsoToDisplayDate(iso)
    }

/** Epoch millis UTC medianoche (compatible con Material DatePicker). */
fun isoDateToUtcEpochMillis(iso: String): Long? {
    val normalized = parseFlexibleDateToIso(iso) ?: return null
    return runCatching {
        Instant.parse("${normalized}T00:00:00Z").toEpochMilliseconds()
    }.getOrNull()
}

fun utcEpochMillisToIsoDate(millis: Long): String =
    Instant.fromEpochMilliseconds(millis).toString().take(10)

private fun isValidIsoParts(iso: String): Boolean {
    val p = iso.split("-")
    if (p.size != 3) return false
    val y = p[0].toIntOrNull() ?: return false
    val m = p[1].toIntOrNull() ?: return false
    val d = p[2].toIntOrNull() ?: return false
    if (y !in 1900..2100 || m !in 1..12 || d !in 1..31) return false
    val maxDay = when (m) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        2 -> if (y % 4 == 0 && (y % 100 != 0 || y % 400 == 0)) 29 else 28
        else -> return false
    }
    return d in 1..maxDay
}
